import { TypeOfObjectValues } from "@validereinc/domain";
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getSmoothStepPath,
} from "@xyflow/react";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import { WorkflowGraphEdgeType } from "./types";
import { WorkflowGraphEdgeMarkerEndMap } from "./WorkflowGraphEdgeMarkers";

import {
  colours,
  Icon,
  TextDataDisplay,
  Tooltip,
} from "@validereinc/common-components";
import { WorkflowGraphCanvasOuterBoundaryId } from "../WorkflowGraphCanvas/WorkflowGraphCanvas";
import { useWorkflowGraphState } from "../WorkflowGraphCanvas/WorkflowGraphStore";
import styles from "./WorkflowGraphEdge.module.scss";
const cx = classNames.bind(styles);

export const WorkflowGraphEdgeOffset = 32;

const WorkflowGraphEdge = ({
  id,
  source,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps<WorkflowGraphEdgeType>) => {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    offset: WorkflowGraphEdgeOffset,
  });

  const isHovered = !!data?.isHovered;
  const isSelected = !!data?.isSelected;

  const { isInEditMode, nodes, stepToCopy } = useWorkflowGraphState();

  const markerStart:
    | TypeOfObjectValues<typeof WorkflowGraphEdgeMarkerEndMap>
    | undefined = useMemo(() => {
    if (isHovered) return WorkflowGraphEdgeMarkerEndMap.ACTIVE;
    return;
  }, [data]);

  const markerEnd: TypeOfObjectValues<typeof WorkflowGraphEdgeMarkerEndMap> =
    useMemo(() => {
      if (data?.isDisabled) return WorkflowGraphEdgeMarkerEndMap.DISABLED;
      if (isHovered || isSelected) return WorkflowGraphEdgeMarkerEndMap.ACTIVE;
      return WorkflowGraphEdgeMarkerEndMap.DEFAULT;
    }, [data]);

  const stateStyles = {
    disabled: data?.isDisabled,
    hover: isHovered,
    selected: isSelected,
  };

  const shouldShowEdgeLabel = !!data?.label || !!data?.icon;
  const sourceNode = nodes.find((node) => node.id === source);
  const isSourceNodeDummyTrigger =
    sourceNode?.data?.isDummy && sourceNode?.data?.variant === "trigger";
  const shouldShowEdgeAction = isInEditMode;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        className={cx({
          default: true,
          ...stateStyles,
          "dummy-trigger-source": isSourceNodeDummyTrigger,
        })}
        markerEnd={`url('#${markerEnd}')`}
        markerStart={`url('#${markerStart}')`}
      />
      <EdgeLabelRenderer>
        {shouldShowEdgeAction && (
          <div
            style={{
              transform: `translate(-50%, 10px) translate(${sourceX}px,${sourceY}px)`,
            }}
            className={`nodrag nopan ${cx({ label: true, edgeAction: true, ...stateStyles })}`}
          >
            <Tooltip
              content={stepToCopy ? "Paste Here" : "Add Step"}
              show={!!data?.isHovered}
              boundaryElementId={WorkflowGraphCanvasOuterBoundaryId}
            >
              <Icon
                variant={stepToCopy ? "arrow-down" : "plus"}
                size={14}
                data-testid="edge-action-button"
                colour={
                  isHovered
                    ? colours.colours.surface[0]
                    : colours.colours.navy[500]
                }
              />
            </Tooltip>
          </div>
        )}
        {shouldShowEdgeLabel && (
          <div
            style={{
              transform: `translate(-50%, -40px) translate(${targetX}px,${targetY}px)`,
            }}
            className={`nodrag nopan ${cx({ label: true, ...stateStyles })}`}
          >
            <Tooltip
              content={data.label}
              show={!!data?.isHovered && !!data.label}
              boundaryElementId={WorkflowGraphCanvasOuterBoundaryId}
            >
              <div className={cx("label-content")}>
                {data?.icon && (
                  <div className={cx("icon-container")}>
                    <Icon
                      variant={data.icon}
                      size={16}
                    />
                  </div>
                )}
                {data?.label && (
                  <TextDataDisplay
                    clamp={1}
                    value={data.label}
                  />
                )}
              </div>
            </Tooltip>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
};

export default WorkflowGraphEdge;
